/**
 * Payroll module help — feature reference for Payroll.jsx screens.
 * Describe WHAT exists and WHERE — not step-by-step process flows.
 */
export const PAYROLL_HELP_TOPICS = [
  {
    id: "payroll-workspace",
    title: "Payroll Workspace",
    summary: "Main payroll list with month filters, insights, and row actions.",
    location: "Payroll → Payroll",
    features: [
      "Employee payroll rows for the selected month / financial year.",
      "Row Actions include Tax Planning, salary revision, loans, and related items (role-gated).",
      "Payroll Action Insights panel for lock / process status items.",
    ],
    snippet: "payroll-workspace",
  },
  {
    id: "tax-planning",
    title: "Tax Planning",
    summary: "Employee tax regime and investment declaration form (Old / New regime).",
    location: "Payroll → row Actions → Tax Planning · or Requests → Request Types → Tax Planning",
    features: [
      "Previous Employment: employer name, PAN, TAN, gross salary, tax deducted.",
      "Old Regime sections: HRA, Section 80C, VIA-deductions, House Property, then Tax liability (last tab).",
      "New Regime: previous employment (if applicable) plus Tax liability illustration on the same page.",
      "Tax liability uses the same payroll TDS engine as Run Payroll → Declarations & TDS; eye icon opens full computation.",
      "Default regime comes from Payroll Settings → Declaration → Tax Year; employees may switch once then lock for the FY (Admin can change anytime).",
      "Submit Declaration starts approval when Declaration workflow is configured.",
      "Editable only when Tax Year is unlocked and Declaration windows / audience allow it.",
      "When Tax Year is locked, unlock it under Payroll → Settings → Declaration before editing.",
    ],
    snippet: "tax-planning",
  },
  {
    id: "payroll-settings-general",
    title: "Settings — General",
    summary: "Service commencement date (read-only).",
    location: "Payroll → Settings → General Settings",
    features: [
      "Service Commences On — company-level, read-only.",
      "Financial year dropdown options start from this service commencement year.",
      "Allow mid month Increments — Salary Revision uses a calendar date when on, or the 1st of the month when off.",
      "Finalise / Unfinalise notifications — optional emails to mapped User Roles (not all employees).",
    ],
    snippet: "settings-general",
  },
  {
    id: "payroll-settings-declaration",
    title: "Settings — Declaration",
    summary: "Tax Year, default regime, Declaration and Proof Submission windows, and approval workflows.",
    location: "Payroll → Settings → Declaration",
    features: [
      "Tax Year — active FY dropdown, Lock / Unlock years grid (locked years block Tax Planning).",
      "Default Tax Regime (New / Old) — starting regime when the year opens; one employee switch per FY after save.",
      "Declaration — month windows (April→March), From/To day, audience (New joinees / All / Selection).",
      "Proof Submission — month windows with Exited / All / Selection audience.",
      "Each section has an approval workflow: Auto approved or Through workflow (roles from User Roles).",
      "Tax Regime Selection Lock — hard company lock; when off, employees get one switch per FY after save (Admin always).",
    ],
    snippet: "settings-declaration",
  },
  {
    id: "run-payroll-monthly-variables",
    title: "Run Payroll — Monthly Variables",
    summary:
      "Enter Monthly Variable components (Method = Monthly Variable on the salary structure) for the pay month via form or Excel.",
    location: "Payroll → Run Payroll → Monthly Variables",
    features: [
      "Component list is a dropdown of salary-structure components with Method = Monthly Variable (legacy: Basis = Recurrent).",
      "Template Excel columns: Sl No, Name, Month, Monthly Variable (dropdown), Amount — Excel only (.xls / .xlsx), not CSV.",
      "Same Monthly Variable cannot be repeated for the same employee in the same month.",
      "Payout % on Add scales the amount (e.g. Performance Bonus at 80% or 120%).",
      "Components with Tax = One Time get separate incremental TDS, then merge with regular TDS on Process.",
    ],
    snippet: "run-payroll-declarations-tds",
  },
  {
    id: "run-payroll-declarations-tds",
    title: "Run Payroll — Declarations and TDS",
    summary: "Review investment declarations and month-on-month TDS before processing payroll.",
    location: "Payroll → Run Payroll → Declarations and TDS (after Monthly Variables)",
    features: [
      "Runs after Monthly Variables so recurrent earnings (Tax = One Time) get incremental TDS merged with regular monthly TDS.",
      "Declarations tab — lists only new joinees and employees with altered / OLD-regime declarations. Default NEW regime shows as status Default.",
      "Declarations tab — Template / Import Excel for investment declarations (Old-regime HRA/80C/VIA/House Property; FY and Tax Regime dropdowns). Upserts live declaration records used by TDS.",
      "TDS Comparison tab — compares computed/applied TDS vs the previous processed payroll month (Δ > ₹1 by default).",
      "TDS Comparison defaults to discrepancies only; check Show all employees to list everyone (table scrolls inside the step).",
      "Eye icon — STI-style TDS computation breakdown (includes One Time tax lines when present). Pencil icon — edit applied TDS for the month (remaining FY months adjust). Pencil is disabled for already processed employees (hover: Unprocess to edit).",
      "TDS Comparison — Template / Import Excel for bulk TDS overrides (Emp Code, FY, Pay Month, Override TDS, Remarks). Same effect as pencil override; processed employees are rejected.",
      "Declarations tab — Export downloads new-joinee grid including TDS Computed for the pay month.",
      "TDS Comparison — Export (enabled only when Show all employees is checked) asks to confirm, then downloads the FY workbook for Active + Joined + Relieved in that FY (not only the month comparison list). Summary has no Pay Month column; tax amounts use the selected pay month as as-of. Sheets: Summary + Component Annexure.",
      "Click Validate to load this step (no auto-check on open). Applied TDS is deducted in Process Payroll.",
      "Employees see tax liability illustration under Payroll → Investment Declaration; admins see it in Tax Planning — both include an eye icon for the same full TDS computation as this step.",
      "Form-16 and Form 24Q Filing under Payroll → Reports.",
    ],
    snippet: "run-payroll-declarations-tds",
  },
  {
    id: "run-payroll-lop-arrears",
    title: "Run Payroll — LOP Reversal & Arrears",
    summary: "Review LOP reversals and salary-revision arrears payable in the selected pay month.",
    location: "Payroll → Run Payroll → LOP Reversal & Arrears (before Declarations and TDS)",
    features: [
      "Runs before Declarations and TDS so arrears / LOP reversal feed into month TDS computation.",
      "LOP reversal amount = pay-days monthly gross of the original LOP month ÷ that month’s calendar days × reversed days (same basis as LOP, which is not a separate deduction line).",
      "Amount column has an eye icon: original LOP month, dates, calendar days, daily rate, and per-component reversal.",
      "Salary revision arrears settle in the Action Date month when Increment Effective is earlier.",
      "Breakdown column shows month-wise arrears text; the eye icon opens present / revised / differential by assigned salary structure component.",
      "Eye view also lists month factors and attendance (present / LOP / paid days) for the settled period(s).",
      "Click Validate again after backend deploy so arrearsDetail is attached to each row.",
    ],
    snippet: "run-payroll-lop-arrears",
  },
  {
    id: "run-payroll-process",
    title: "Run Payroll — Process Payroll",
    summary: "Process employee salaries for the month and export Quick Reports workbooks.",
    location: "Payroll → Run Payroll → Process Payroll",
    features: [
      "Process / Reprocess / Unprocess employees after prior validation steps pass.",
      "Pay Days components are earned as Monthly ÷ Calendar Days × Paid Days (no separate LOP Deduction line). Recurrent components stay full month and add to gross/deductions.",
      "Manual vs Auto salary structure: Process uses the employee snapshot. Manual with missing/zero breakdown is blocked (enter amounts in Profile eye or Payroll → Manual Component Import). Auto may rebuild from the matched template + CTC only.",
      "If Process leaves people on Unprocessed, open the Failed tab — errors (e.g. no first salary map) are listed there. New Joinees cannot be Ignored while Salary Structure is still missing.",
      "Professional Tax (PT) — earned gross after attendance (incl. monthly variables) → branch/state slab → full slab amount (PT itself is never prorated). Same amount on Process eye, Payroll Register Excel, and Payroll Projection.",
      "Quick Reports dropdown (above the grid): Projection, Register, Reconciliation, Accounting Journal.",
      "Payroll Projection — requires every in-scope employee to be processed (24 Jul instruction); otherwise a popup asks you to process all first. Uses DOJ/DOE payable days.",
      "Payroll Register / Reconciliation / Accounting Journal — export allowed once at least one employee is processed (processed rows only); does not require the full scope.",
      "Payroll Projection uses DOJ/DOE payable days (stored in DB) + Salary Summary sheet that must tie to the register.",
      "Register / Projection include Total Cost (Gross + Employer PF + Employer ESI + Gratuity + other employer benefits) and Total Cash Flow (Net + structured deductions excl. one-time + Employer PF + Employer ESI).",
      "Payroll Reconciliation exports as Reco Report {Month}.xlsx — Opening (A), Joiners (B), Left (C), Adjustments (D) with reason text, Reason Summary sheet, and component detail.",
      "Payroll Register = Register + Salary Summary + Bank Statement. Accounting Journal = JV + Register replica with cost match.",
    ],
    snippet: "run-payroll-process",
  },
  {
    id: "run-payroll-finalise",
    title: "Run Payroll — Finalise",
    summary: "Lock the pay month, unlock in sequence, and review finalise history.",
    location: "Payroll → Run Payroll → Finalise",
    features: [
      "Finalise locks calculation for the month. Payment register can stay open.",
      "Unlock only if the next month is already unfinalised (or has no run). You cannot skip backwards.",
      "You cannot finalise a later month while an earlier opened month is still unfinalised.",
      "History (read-only) lists Finalised / Unfinalised / Requested / Approved / Rejected with actor, date, and note.",
    ],
    snippet: "run-payroll-process",
  },
  {
    id: "loan-leave-encashment",
    title: "Loan and Leave Encashment",
    summary:
      "Loan and Leave Encashment actions, policies under Payroll Settings, and the operator guide.",
    location: "Payroll → Run Payroll → Actions · Settings → Policies",
    features: [
      "Configure policies: Payroll → Settings → Policies. Use the list (same pattern as Separation Policy). New / Edit opens a step wizard. Hover ⓘ on each field for meaning.",
      "Loan application: Payroll → Run Payroll → Actions → Loan. Fill amount, tenure, purpose, disbursement date, remarks. Submit. Policies are chosen on the form, not configured there.",
      "Leave encashment request: Actions → Leave Encashment. Fill leave type, days, pay month, remarks. Leave Group must allow encashment.",
      "Do not enter employer loans in Tax Planning 80C / house property.",
    ],
    snippet: "loan-leave-encashment",
  },
  {
    id: "request-types-tax-planning",
    title: "Request Types — Tax Planning",
    summary: "Employee entry to the same Tax Planning form from the right sidebar.",
    location: "Right sidebar Requests → Request Types → Tax Planning",
    features: [
      "Opens the same Tax Planning modal used from Payroll Actions.",
      "Old Regime: complete declaration tabs, then Tax liability last; New Regime: illustration on the same page with eye for TDS computation.",
      "Edit access follows Tax Year lock and Declaration windows for the current calendar month.",
      "Approvers see pending submissions when Declaration/Proof workflow uses their role.",
    ],
    snippet: "request-types",
  },
  {
    id: "salary-structure-manual-import",
    title: "Import — Manual Structure Components",
    summary:
      "Bulk-enter Manual salary-structure component amounts so Process uses the employee snapshot (not CTC rebuild).",
    location: "Payroll → Import → Manual Component Import (or Workspace Import)",
    features: [
      "Use when the assigned structure is Manual (not Auto/Formula).",
      "Template columns are the Manual fixed heads for the selected structure plus Emp Code / CTC.",
      "Import writes EmployeeSalaryStructure resolved lines + manualOverrides; Gross mode matches Subtotal (E) to Profile Gross, CTC mode matches E+B to Profile CTC.",
      "Auto (Formula) employees do not need this — CTC alone drives the snapshot.",
      "Salary revision (single or Excel) refreshes the snapshot: Manual amounts are entered as monthly ₹ (0 allowed; never auto-scaled by CTC); Auto recomputes from formulas.",
    ],
    snippet: "reports-import",
  },
  {
    id: "reports-import",
    title: "Reports, Import & Statutory Filing",
    summary:
      "Payslip reports, Form-16, statutory filing (PF/ESI/PT/Form 24Q), and investment declaration import.",
    location: "Payroll → Reports / Import / Statutory Filing / Export",
    features: [
      "Salary Revision Report and Form-16 under Reports.",
      "Statutory Filing — PF, ESI, PT from finalised payroll months only; EPFO ECR (#~# delimited), ESIC MC_Template11.xls, PT portal Excel; Form 24Q Filing wizard.",
      "Import — Salary Revision (Excel: Amount/%age or Manual; Increment Effective Date/Month, Pay Month ≥ effective unfinalised month, Continue Same Month Increment). Structure snapshot refreshes on import. Investment Declaration Import (template + upload modal).",
      "Import — Manual Component Import for Manual salary structures (component ₹ amounts).",
      "Form 24Q Filing — 5-step wizard: Period (Port Data), Review, Challans & Link, RPU/FVU, Record filing.",
      "Download Template is available inside Investment Declaration Import modal; Export remains on the sidebar.",
      "PF admin charges use Statutory Rates Admin Charges % with establishment minimum (default ₹500) on statement totals.",
    ],
    snippet: "reports-import",
  },
];
